/**
 * useAdSettings — the ad switches from GET /api/settings
 * (Admin > Settings > Ads). Fetched once per page load and shared by every
 * ad unit; admin saves call invalidateAdSettings() so the next read is fresh.
 *
 * Returns { enabled, client, slot, multiplexEnabled, multiplexSlot }.
 * `enabled` (in-article) is true only when ads are switched on AND the
 * in-article slot ID is set; `multiplexEnabled` likewise needs ads on AND
 * the Multiplex slot ID. Until the fetch resolves (or if it fails) ads
 * stay off.
 */
import { useEffect, useState } from 'react';
import { getSettings } from '@/lib/api';
import { normalise, ADSENSE_CLIENT } from '@/lib/adSettings';

export { ADSENSE_CLIENT };

const OFF = normalise(); // every unit off

let pending = null; // Promise of the normalised settings
let resolved = null; // the same value once it has arrived

export function loadAdSettings() {
  if (!pending) {
    pending = getSettings()
      .then((res) => normalise(res?.data))
      .catch(() => OFF)
      .then((v) => {
        resolved = v;
        return v;
      });
  }
  return pending;
}

export function invalidateAdSettings() {
  pending = null;
  resolved = null;
}

export default function useAdSettings() {
  const [settings, setSettings] = useState(resolved || OFF);
  useEffect(() => {
    let live = true;
    loadAdSettings().then((v) => {
      if (live) setSettings(v);
    });
    return () => {
      live = false;
    };
  }, []);
  return settings;
}
